import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import StarRatingComponent from 'react-star-rating-component';
import Helmet from 'react-helmet';

import AuthService from './AuthService';
import AuthModalContext from '../contexts/AuthModalContext';
import CartContext from '../contexts/CartContext';
import { GameContext } from '../contexts/GameContext';
import LazyCardImage from './LazyCardImage';
import PriceHistoryChart from './PriceHistoryChart';
import { renderRarityBadge } from '../utils/rarityBadge';
import { LEGACY_NO_FOIL_RARITIES } from '../utils/conditions';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';

const Auth = new AuthService();

const TAG_SUGGESTIONS = ['Staple', 'Budget Pick', 'Combo Piece', 'Meta', 'Underrated', 'Fan Favorite'];

const SORT_OPTIONS = [
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'shipping-asc', label: 'Shipping: Low to High' },
  { value: 'total-asc', label: 'Total: Low to High' },
  { value: 'rating-desc', label: 'Rating: Best First' },
  { value: 'foil-desc', label: 'Foil First' },
  { value: 'foil-asc', label: 'Non-Foil First' },
];

class CardDetail extends Component {
  static contextType = AuthModalContext;

  constructor(props) {
    super(props);
    this.state = {
      card: null,
      listings: [],
      loading: true,
      error: null,
      history: [],
      relatedCards: [],
      tags: [],
      alertSet: false,
      alertFoilOnly: false,
      showAllTags: false,
      showTagInput: false,
      tagInputValue: '',
      sortBy: 'price',
      sortDir: 'asc',
      sortOpen: false,
      historyFilter: 'all',
      listingPage: 0,
      listingsPerPage: 6,
      listingFading: false,
      followingList: [],
      buylistDemand: null,
      showAllBuylistEntries: false,
      // Progressive disclosure (halo effect)
      showPriceHistory: false,
      showCommunityTags: false,
    };
    this.cardId = null;
    this.listingsRef = null;
    this.listingsSectionRef = React.createRef();
    this.listingsMinHeight = 0;
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const cardId = this.props.match && this.props.match.params.card_id;
    if (cardId) {
      this.cardId = cardId;
      this.fetchCard(this.cardId);
      this.checkAlertStatus(this.cardId);
    }
    this.fetchFollowingList();
    this.handleAuthChange = () => {
      if (this.cardId) {
        if (!Auth.loggedIn()) {
          this.setState({ alertSet: false, followingList: [] });
        } else {
          this.checkAlertStatus(this.cardId);
          this.fetchFollowingList();
        }
      }
    };
    window.addEventListener('authStateChanged', this.handleAuthChange);
    this._handleThemeChanged = function() { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);
  }

  componentWillUnmount() {
    window.removeEventListener('authStateChanged', this.handleAuthChange);
    window.removeEventListener('themeChanged', this._handleThemeChanged);
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match && prevProps.match.params.card_id;
    const currId = this.props.match && this.props.match.params.card_id;
    if (currId && currId !== prevId) {
      this.cardId = currId;
      this.listingsMinHeight = 0;
      this.setState({ loading: true, card: null, listings: [], history: [], relatedCards: [], tags: [], sortOpen: false, listingPage: 0, alertSet: false, alertFoilOnly: false });
      this.fetchCard(this.cardId);
      this.checkAlertStatus(this.cardId);
      window.scrollTo(0, 0);
    }
  }

  checkAlertStatus(cardId) {
    if (!Auth.loggedIn()) return;
    Auth.fetch(`${constants.DOMAIN}/api/v1/pricewatches`, {
      mode: 'cors',
      method: 'GET',
    })
      .then(res => {
        var watches = res.watches || [];
        var match = watches.find(function(w) {
          var wCardId = w.card && typeof w.card === 'object' ? w.card._id : w.card;
          return String(wCardId) === String(cardId);
        });
        if (match) {
          this.setState({ alertSet: true, alertFoilOnly: match.foilOnly || false });
        } else {
          this.setState({ alertSet: false, alertFoilOnly: false });
        }
      })
      .catch(() => {});
  }

  fetchFollowingList() {
    if (!Auth.loggedIn()) return;
    try {
      const user = Auth.getProfile();
      Auth.fetch(`${constants.DOMAIN}/api/v1/users/${user._id}`, {
        mode: 'cors',
        method: 'GET',
      })
        .then(res => {
          this.setState({ followingList: (res.profile && res.profile.isFollowing) || [] });
        })
        .catch(() => {});
    } catch (e) { /* ignore */ }
  }

  isFollowingSeller(sellerId) {
    if (!sellerId) return false;
    return this.state.followingList.some(id => String(id) === String(sellerId));
  }

  isSellerInCart(sellerId, cart) {
    if (!sellerId || !cart || cart.length === 0) return false;
    return cart.some(function(item) { return String(item.sellerId) === String(sellerId); });
  }

  togglePriceAlert() {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login', () => this.togglePriceAlert());
      return;
    }
    var cardId = this.cardId;
    var self = this;
    var alertSet = this.state.alertSet;
    var alertFoilOnly = this.state.alertFoilOnly;

    if (alertSet) {
      // Remove the watch
      fetch(`${constants.DOMAIN}/api/v1/pricewatches/${cardId}`, {
        mode: 'cors',
        method: 'DELETE',
        headers: { 'Authorization': Auth.getAuthHeader() },
      })
        .then(function() {
          self.setState({ alertSet: false, alertFoilOnly: false });
        })
        .catch(function() {});
    } else {
      // Create the watch
      fetch(`${constants.DOMAIN}/api/v1/pricewatches`, {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': Auth.getAuthHeader(),
        },
        body: JSON.stringify({ cardId: cardId, foilOnly: alertFoilOnly }),
      })
        .then(function(r) { return r.json(); })
        .then(function() {
          self.setState({ alertSet: true });
        })
        .catch(function() {});
    }
  }

  toggleAlertFoilOnly() {
    var cardId = this.cardId;
    var alertSet = this.state.alertSet;
    var newFoilOnly = !this.state.alertFoilOnly;
    this.setState({ alertFoilOnly: newFoilOnly });
    if (alertSet) {
      // Re-create the watch with updated foilOnly preference
      fetch(`${constants.DOMAIN}/api/v1/pricewatches`, {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': Auth.getAuthHeader(),
        },
        body: JSON.stringify({ cardId: cardId, foilOnly: newFoilOnly }),
      })
        .then(function() {})
        .catch(function() {});
    }
  }

  goToPage(page) {
    if (page === this.state.listingPage) return;
    if (this.listingsRef) {
      var h = this.listingsRef.offsetHeight;
      if (h > this.listingsMinHeight) this.listingsMinHeight = h;
    }
    this.setState({ listingFading: true });
    setTimeout(() => {
      this.setState({ listingPage: page, listingFading: false });
    }, 150);
  }

  setSort(field) {
    const { sortBy, sortDir } = this.state;
    if (sortBy === field) {
      this.setState({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      this.setState({ sortBy: field, sortDir: 'asc' });
    }
  }

  getSortedListings() {
    const { listings, sortBy, sortDir } = this.state;
    const sorted = [...listings].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'price') {
        valA = a.price || 0;
        valB = b.price || 0;
      } else if (sortBy === 'shipping') {
        valA = a.shippingCost || 0;
        valB = b.shippingCost || 0;
      } else if (sortBy === 'total') {
        valA = (a.price || 0) + (a.shippingCost || 0);
        valB = (b.price || 0) + (b.shippingCost || 0);
      } else if (sortBy === 'rating') {
        valA = a.seller ? (a.seller.rating || 0) : 0;
        valB = b.seller ? (b.seller.rating || 0) : 0;
      } else if (sortBy === 'foil') {
        valA = a.foil ? 1 : 0;
        valB = b.foil ? 1 : 0;
      } else {
        return 0;
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
    return sorted;
  }

  fetchCard(cardId) {
    const cardUrl = `${constants.DOMAIN}/api/v1/guest/card/${cardId}`;
    const listingsUrl = `${constants.DOMAIN}/api/v1/guest/card/${cardId}/listings`;
    const historyUrl = `${constants.DOMAIN}/api/v1/guest/card/${cardId}/history`;
    const relatedUrl = `${constants.DOMAIN}/api/v1/guest/card/${cardId}/related`;
    const userId = Auth.loggedIn() ? Auth.getProfile()._id : null;
    const tagsUrl = `${constants.DOMAIN}/api/v1/guest/card-tags/${cardId}${userId ? '?userId=' + userId : ''}`;
    // BUYLIST FEATURE: disabled — preserved for future use
    // const buylistUrl = `${constants.DOMAIN}/api/v1/guest/card/${cardId}/buylist`;

    Promise.all([
      fetch(cardUrl, { mode: 'cors', method: 'GET' }).then(r => r.json()),
      fetch(listingsUrl, { mode: 'cors', method: 'GET' }).then(r => r.json()),
      fetch(historyUrl, { mode: 'cors', method: 'GET' }).then(r => r.json()).catch(() => ({ history: [] })),
      fetch(relatedUrl, { mode: 'cors', method: 'GET' }).then(r => r.json()).catch(() => ({ related: [] })),
      fetch(tagsUrl, { mode: 'cors', method: 'GET' }).then(r => r.json()).catch(() => ({ tags: [] })),
      // BUYLIST FEATURE: disabled — preserved for future use
      // fetch(buylistUrl, { mode: 'cors', method: 'GET' }).then(r => r.json()).catch(() => ({ totalQuantity: 0, highestPrice: 0, buyerCount: 0, entries: [] })),
    ])
      .then(([cardRes, listingsRes, historyRes, relatedRes, tagsRes]) => {
        this.setState({
          card: cardRes,
          listings: listingsRes.listings || [],
          history: historyRes.history || [],
          relatedCards: relatedRes.related || [],
          tags: tagsRes.tags || [],
          // BUYLIST FEATURE: disabled — preserved for future use
          // buylistDemand: buylistRes,
          loading: false,
        });
      })
      .catch(err => {
        console.log('Error fetching card:', err);
        this.setState({ loading: false, error: 'Failed to load card.' });
      });
  }

  fetchTags() {
    const userId = Auth.loggedIn() ? Auth.getProfile()._id : null;
    const tagsUrl = `${constants.DOMAIN}/api/v1/guest/card-tags/${this.cardId}${userId ? '?userId=' + userId : ''}`;
    fetch(tagsUrl, { mode: 'cors', method: 'GET' })
      .then(r => r.json())
      .then(res => this.setState({ tags: res.tags || [] }))
      .catch(() => {});
  }

  addTag(tagText) {
    if (!Auth.loggedIn()) return;
    const tag = tagText.trim().toLowerCase();
    if (!tag) return;

    fetch(`${constants.DOMAIN}/api/v1/card-tags/${this.cardId}`, {
      mode: 'cors',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': Auth.getAuthHeader(),
      },
      body: JSON.stringify({ tag }),
    })
      .then(r => r.json())
      .then(() => {
        this.setState({ showTagInput: false, tagInputValue: '' });
        this.fetchTags();
      })
      .catch(() => {});
  }

  removeTag(tag) {
    if (!Auth.loggedIn()) return;

    fetch(`${constants.DOMAIN}/api/v1/card-tags/${this.cardId}/${encodeURIComponent(tag)}`, {
      mode: 'cors',
      method: 'DELETE',
      headers: {
        'Authorization': Auth.getAuthHeader(),
      },
    })
      .then(r => r.json())
      .then(() => this.fetchTags())
      .catch(() => {});
  }

  toggleTag(tag) {
    if (!Auth.loggedIn()) return;
    if (tag.userVoted) {
      this.removeTag(tag.tag);
    } else {
      this.addTag(tag.tag);
    }
  }

  // renderRarityBadge imported from utils/rarityBadge

  renderElementBadges(elements) {
    if (!elements || elements.length === 0) return null;
    return elements.map((el, i) => (
      <span
        key={i}
        className="tw-text-xs tw-font-medium tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-[#f5f5f5] dark:tw-bg-[#252525] tw-text-[#666] dark:tw-text-gray-400 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333]"
      >
        {el}
      </span>
    ));
  }

  renderDynamicFields(card, gameConfig) {
    if (!gameConfig || !gameConfig.cardFields) return this.renderLegacyFields(card);

    const detailFields = gameConfig.cardFields
      .filter(f => f.showInDetail)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    // Separate badge fields (element, cardType) and hide game-stat fields from detail view
    const badgeKeys = ['element', 'cardType'];
    const hideKeys = ['element', 'cardType', 'characteristic', 'healthy', 'injured'];
    const statFields = detailFields.filter(f => !hideKeys.includes(f.key));

    if (statFields.length === 0) return null;

    // Check if any stat field has a value
    const hasAnyValue = statFields.some(f => {
      const val = card[f.key];
      if (val == null) return false;
      if (Array.isArray(val)) return val.length > 0;
      return val !== '';
    });
    if (!hasAnyValue) return null;

    return (
      <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-4">
        {statFields.map(field => {
          const val = card[field.key];
          if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) return null;

          if (field.type === 'array' && Array.isArray(val)) {
            return (
              <div key={field.key} className="tw-text-sm">
                <span className="tw-text-[#999] dark:tw-text-gray-500">{field.label}: </span>
                {val.map((item, i) => (
                  <span key={i} className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">
                    {item}{i < val.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            );
          }

          return (
            <div key={field.key} className="tw-text-sm">
              <span className="tw-text-[#999] dark:tw-text-gray-500">{field.label}: </span>
              <span className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{val}</span>
            </div>
          );
        })}
      </div>
    );
  }

  renderLegacyFields(card) {
    if (!card.handCost && !card.turnCount && !card.abilityCost) return null;
    return (
      <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mb-4">
        {card.handCost && (
          <div className="tw-text-sm">
            <span className="tw-text-[#999] dark:tw-text-gray-500">Hand Cost: </span>
            <span className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{card.handCost}</span>
          </div>
        )}
        {card.turnCount && (
          <div className="tw-text-sm">
            <span className="tw-text-[#999] dark:tw-text-gray-500">Turn Count: </span>
            <span className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{card.turnCount}</span>
          </div>
        )}
        {card.abilityCost && card.abilityCost.length > 0 && (
          <div className="tw-text-sm">
            <span className="tw-text-[#999] dark:tw-text-gray-500">Ability Cost: </span>
            {card.abilityCost.map((c, i) => (
              <span key={i} className="tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">
                {c}{i < card.abilityCost.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderLoading() {
    return (
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-12">
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-8">
          <div className="tw-w-full md:tw-w-[40%]">
            <div className="tw-aspect-[3/4] tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded-xl tw-animate-pulse" />
          </div>
          <div className="tw-flex-1">
            <div className="tw-h-5 tw-w-32 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-3 tw-animate-pulse" />
            <div className="tw-h-8 tw-w-64 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-4 tw-animate-pulse" />
            <div className="tw-h-4 tw-w-24 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-3 tw-animate-pulse" />
            <div className="tw-flex tw-gap-2 tw-mb-6">
              <div className="tw-h-6 tw-w-16 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded-full tw-animate-pulse" />
              <div className="tw-h-6 tw-w-16 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded-full tw-animate-pulse" />
            </div>
            <div className="tw-h-6 tw-w-40 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-animate-pulse" />
          </div>
        </div>
        <div className="tw-mt-10">
          <div className="tw-h-6 tw-w-48 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-4 tw-animate-pulse" />
          {[1, 2, 3].map(i => (
            <div key={i} className="tw-h-16 tw-w-full tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded-lg tw-mb-3 tw-animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  renderSellerBadges(seller) {
    if (!seller) return null;
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <div className="tw-flex tw-items-center" style={{ fontSize: '0.7rem' }}>
          <StarRatingComponent
            name={`rating-${seller._id}`}
            editing={false}
            starCount={5}
            starColor="#c9a84c"
            emptyStarColor="#555"
            value={seller.rating || 0}
          />
        </div>
        {seller.saleCount > 0 && (
          <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500">
            {seller.saleCount} sale{seller.saleCount !== 1 ? 's' : ''}
          </span>
        )}
        {seller.verified && (
          <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-medium tw-text-brand-gold">
            <svg className="tw-w-3.5 tw-h-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Verified
          </span>
        )}
      </div>
    );
  }

  isOwnListing(listing) {
    try {
      if (!Auth.loggedIn()) return false;
      var user = Auth.getProfile();
      var sellerId = listing.seller ? listing.seller._id : null;
      return sellerId && String(user._id) === String(sellerId);
    } catch (e) { return false; }
  }

  handleAddToCart(listing, cartCtx) {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login');
      return;
    }
    if (this.isOwnListing(listing)) return;
    var seller = listing.seller || {};
    var card = this.state.card || {};
    var item = {
      postId: listing._id,
      title: listing.title,
      price: listing.price,
      shippingCost: listing.shippingCost || 0,
      image: listing.images && listing.images.length > 0 ? listing.images[0] : (listing.cardImage || ''),
      sellerId: seller._id,
      sellerName: seller.userName,
      rarity: card.rarity || null,
      cardId: card._id || this.cardId || null,
      condition: listing.category ? listing.category.replace(/^singles\s*/i, '').replace(/F$/i, '').trim() || 'NM' : null,
      foil: listing.foil || false,
      availableQuantity: listing.quantity || 1,
    };
    cartCtx.addToCart(item);
    window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Added to cart!', type: 'success' } }));
  }

  renderListingRow(listing, cart) {
    const seller = listing.seller;
    const defaultAvatar = 'https://static.productionready.io/images/smiley-cyrus.jpg';
    const avatarUrl = seller && seller.image ? (
      seller.image.startsWith('http') ? seller.image : `${constants.DOMAIN}/${seller.image}`
    ) : defaultAvatar;
    const isCartSeller = seller ? this.isSellerInCart(seller._id, cart) : false;

    return (
      <tr key={listing._id} className="tw-border-b tw-border-[#e0e0e0] dark:tw-border-[#333] last:tw-border-b-0" style={isCartSeller ? { backgroundColor: 'rgba(34,197,94,0.06)' } : undefined}>
        {/* Seller */}
        <td className="tw-py-3 tw-px-3">
          {seller ? (
            <div>
              <Link to={`/shop/${seller.userName}`} className="tw-flex tw-items-center tw-gap-2.5 tw-no-underline tw-group">
                <img
                  src={avatarUrl}
                  alt={seller.userName}
                  className="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    var initials = (seller.userName || '?').split(' ').map(function(w) { return w[0]; }).join('').toUpperCase().slice(0, 2);
                    var fallback = document.createElement('div');
                    fallback.textContent = initials;
                    fallback.style.cssText = 'width:32px;height:32px;border-radius:50%;background:#c9a84c;color:#000;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;flex-shrink:0;';
                    e.target.parentNode.insertBefore(fallback, e.target);
                  }}
                />
                <div className="tw-min-w-0">
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <p className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white group-hover:tw-text-brand-gold tw-transition-colors tw-truncate tw-mb-0">
                      {seller.userName}
                    </p>
                    {this.isFollowingSeller(seller._id) && (
                      <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-brand-gold/15 tw-text-brand-gold tw-whitespace-nowrap">
                        Following
                      </span>
                    )}
                    {isCartSeller && (
                      <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-whitespace-nowrap" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#16a34a' }}>
                        In Your Cart
                      </span>
                    )}
                  </div>
                  {this.renderSellerBadges(seller)}
                </div>
              </Link>
              {seller.otherListings > 0 && (
                <Link
                  to={`/shop/${seller.userName}`}
                  className="tw-text-xs tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline tw-mt-1 tw-ml-10.5 tw-inline-block"
                  style={{ marginLeft: '42px' }}
                >
                  View {seller.otherListings} more listing{seller.otherListings !== 1 ? 's' : ''} &rarr;
                </Link>
              )}
            </div>
          ) : (
            <span className="tw-text-sm tw-text-[#999]">Unknown Seller</span>
          )}
        </td>
        {/* Condition */}
        <td className="tw-py-3 tw-px-3">
          <div className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400">
              NM
            </span>
            {listing.foil && (
              <span
                className="tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-full"
                style={{ background: 'linear-gradient(90deg, #a8a8a8, #d4d4d4, #a8a8a8)', color: '#2a2a2a' }}
              >
                FOIL
              </span>
            )}
          </div>
        </td>
        {/* Price */}
        <td className="tw-py-3 tw-px-3 tw-text-center">
          <span className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
            ${listing.price ? listing.price.toFixed(2) : '0.00'}
          </span>
          {(listing.quantity || 1) > 1 && (
            <div className="tw-mt-0.5">
              <span className="tw-text-[11px] tw-font-semibold tw-text-brand-gold tw-bg-brand-gold/10 tw-px-2 tw-py-0.5 tw-rounded-full">
                {listing.quantity} available
              </span>
            </div>
          )}
        </td>
        {/* Shipping */}
        <td className="tw-py-3 tw-px-3">
          {listing.price >= (constants.FREE_SHIPPING_THRESHOLD || 10) ? (
            <span className="tw-text-xs tw-font-medium tw-text-green-600 dark:tw-text-green-400">
              Free Shipping
            </span>
          ) : (
            <span className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500">
              $1 shipping (free over $10)
            </span>
          )}
        </td>
        {/* View + Buy */}
        <td className="tw-py-3 tw-px-3 tw-text-right">
          <Link
            to={{ pathname: `/product/${listing._id}`, state: { fromCardId: this.cardId } }}
            className="tw-inline-block tw-border tw-border-brand-gold tw-bg-white dark:tw-bg-[#1a1a1a] tw-text-brand-gold hover:tw-bg-brand-gold/15 dark:hover:tw-bg-brand-gold/20 tw-font-bold tw-py-2 tw-px-5 tw-rounded-lg tw-text-xs tw-transition-colors tw-mr-2"
            style={{ textDecoration: 'none', color: '#c9a84c' }}
          >
            View Listing
          </Link>
          {this.isOwnListing(listing) ? (
            <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-italic">Your listing</span>
          ) : (
            <>
              <CartContext.Consumer>
                {(cartCtx) => {
                  var cartItem = cartCtx.cart.find(function(ci) { return ci.postId === listing._id; });
                  if (cartItem) {
                    var cartQty = cartItem.quantity || 1;
                    var maxQty = listing.quantity || 1;
                    return (
                      <span className="tw-inline-flex tw-items-center tw-border tw-border-green-300 dark:tw-border-green-800 tw-rounded-lg tw-text-xs tw-mr-2 tw-overflow-hidden" style={{ backgroundColor: 'rgba(34,197,94,0.08)' }}>
                        <button
                          onClick={function() {
                            if (cartQty <= 1) { cartCtx.removeFromCart(listing._id); window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Removed from cart', type: 'info' } })); }
                            else { cartCtx.updateCartQuantity(listing._id, cartQty - 1, maxQty); }
                          }}
                          className="tw-px-2 tw-py-2 tw-bg-transparent tw-border-0 tw-text-green-700 dark:tw-text-green-400 hover:tw-bg-green-100 dark:hover:tw-bg-green-900/30 tw-transition-colors tw-cursor-pointer tw-font-bold tw-text-sm tw-leading-none"
                        >
                          {cartQty <= 1 ? <svg className="tw-w-3 tw-h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg> : '\u2212'}
                        </button>
                        <span className="tw-px-2 tw-py-2 tw-font-bold tw-text-green-700 dark:tw-text-green-400 tw-min-w-[24px] tw-text-center">{cartQty}</span>
                        <button
                          onClick={function() { if (cartQty < maxQty) { cartCtx.updateCartQuantity(listing._id, cartQty + 1, maxQty); } }}
                          disabled={cartQty >= maxQty}
                          className={"tw-px-2 tw-py-2 tw-bg-transparent tw-border-0 tw-font-bold tw-text-sm tw-leading-none tw-transition-colors" + (cartQty >= maxQty ? " tw-text-green-300 dark:tw-text-green-800 tw-cursor-default" : " tw-text-green-700 dark:tw-text-green-400 hover:tw-bg-green-100 dark:hover:tw-bg-green-900/30 tw-cursor-pointer")}
                        >
                          +
                        </button>
                      </span>
                    );
                  }
                  return (
                    <button
                      onClick={() => this.handleAddToCart(listing, cartCtx)}
                      className="tw-inline-block tw-border tw-border-brand-gold tw-bg-transparent tw-text-brand-gold hover:tw-bg-brand-gold/10 tw-font-bold tw-py-2 tw-px-4 tw-rounded-lg tw-text-xs tw-transition-colors tw-cursor-pointer tw-mr-2"
                    >
                      Add to Cart
                    </button>
                  );
                }}
              </CartContext.Consumer>
              <Link
                to={`/purchase/${listing._id}`}
                className="tw-inline-block tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-font-bold tw-py-2 tw-px-5 tw-rounded-lg tw-text-xs tw-transition-colors"
                style={{ textDecoration: 'none', color: '#000' }}
              >
                Buy Now
              </Link>
            </>
          )}
        </td>
      </tr>
    );
  }

  renderListingCard(listing, cart) {
    const seller = listing.seller;
    const defaultAvatar = 'https://static.productionready.io/images/smiley-cyrus.jpg';
    const avatarUrl = seller && seller.image ? (
      seller.image.startsWith('http') ? seller.image : `${constants.DOMAIN}/${seller.image}`
    ) : defaultAvatar;
    const total = (listing.price || 0) + (listing.shippingCost || 0);
    const isCartSeller = seller ? this.isSellerInCart(seller._id, cart) : false;

    return (
      <div key={listing._id} className="tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-p-4" style={isCartSeller ? { borderColor: 'rgba(34,197,94,0.35)', backgroundColor: 'rgba(34,197,94,0.04)' } : undefined}>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          {seller ? (
            <div className="tw-flex-1 tw-min-w-0">
              <Link to={`/shop/${seller.userName}`} className="tw-flex tw-items-center tw-gap-2.5 tw-no-underline tw-group">
                <img
                  src={avatarUrl}
                  alt={seller.userName}
                  className="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover tw-border tw-border-[#e0e0e0] dark:tw-border-[#333]"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    var initials = (seller.userName || '?').split(' ').map(function(w) { return w[0]; }).join('').toUpperCase().slice(0, 2);
                    var fallback = document.createElement('div');
                    fallback.textContent = initials;
                    fallback.style.cssText = 'width:32px;height:32px;border-radius:50%;background:#c9a84c;color:#000;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;flex-shrink:0;';
                    e.target.parentNode.insertBefore(fallback, e.target);
                  }}
                />
                <div>
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <p className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white group-hover:tw-text-brand-gold tw-transition-colors tw-mb-0">
                      {seller.userName}
                    </p>
                    {seller.verified && (
                      <span className="tw-text-brand-gold">
                        <svg className="tw-w-3.5 tw-h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                    {this.isFollowingSeller(seller._id) && (
                      <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-brand-gold/15 tw-text-brand-gold tw-whitespace-nowrap">
                        Following
                      </span>
                    )}
                    {isCartSeller && (
                      <span className="tw-text-[10px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-whitespace-nowrap" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#16a34a' }}>
                        In Your Cart
                      </span>
                    )}
                  </div>
                  {this.renderSellerBadges(seller)}
                </div>
              </Link>
              {seller.otherListings > 0 && (
                <Link
                  to={`/shop/${seller.userName}`}
                  className="tw-text-xs tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline tw-mt-1.5 tw-inline-block"
                  style={{ marginLeft: '42px' }}
                >
                  View {seller.otherListings} more listing{seller.otherListings !== 1 ? 's' : ''} &rarr;
                </Link>
              )}
            </div>
          ) : (
            <span className="tw-text-sm tw-text-[#999]">Unknown Seller</span>
          )}
          <div className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500">NM</span>
            {listing.foil && (
              <span
                className="tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-full"
                style={{ background: 'linear-gradient(90deg, #a8a8a8, #d4d4d4, #a8a8a8)', color: '#2a2a2a' }}
              >
                FOIL
              </span>
            )}
          </div>
        </div>
        {/* Price row */}
        <div className="tw-flex tw-items-baseline tw-justify-between tw-mb-3">
          <div>
            <span className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
              ${listing.price ? listing.price.toFixed(2) : '0.00'}
            </span>
            {listing.shippingCost > 0 ? (
              <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-ml-1.5">
                + ${listing.shippingCost.toFixed(2)} ship
              </span>
            ) : (
              <span className="tw-text-xs tw-font-medium tw-text-green-600 dark:tw-text-green-400 tw-ml-1.5">
                Free ship
              </span>
            )}
          </div>
          <div className="tw-text-right">
            <span className="tw-text-sm tw-font-medium tw-text-[#666] dark:tw-text-gray-400">
              Total: ${total.toFixed(2)}
            </span>
            {(listing.quantity || 1) > 1 && (
              <p className="tw-text-xs tw-font-semibold tw-text-brand-gold tw-mb-0 tw-mt-0.5">{listing.quantity} available</p>
            )}
          </div>
        </div>
        {/* Action buttons - full width row */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <Link
            to={{ pathname: `/product/${listing._id}`, state: { fromCardId: this.cardId } }}
            className="tw-border tw-border-brand-gold tw-bg-white dark:tw-bg-[#1a1a1a] tw-text-brand-gold hover:tw-bg-brand-gold/15 dark:hover:tw-bg-brand-gold/20 tw-font-bold tw-py-2.5 tw-px-4 tw-rounded-lg tw-text-sm tw-transition-colors"
            style={{ textDecoration: 'none', color: '#c9a84c' }}
          >
            View
          </Link>
          {this.isOwnListing(listing) ? (
            <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-italic">Your listing</span>
          ) : (
            <>
              <CartContext.Consumer>
                {(cartCtx) => {
                  var cartItem = cartCtx.cart.find(function(ci) { return ci.postId === listing._id; });
                  if (cartItem) {
                    var cartQty = cartItem.quantity || 1;
                    var maxQty = listing.quantity || 1;
                    return (
                      <span className="tw-inline-flex tw-items-center tw-border tw-border-green-300 dark:tw-border-green-800 tw-rounded-lg tw-text-sm tw-overflow-hidden" style={{ backgroundColor: 'rgba(34,197,94,0.08)' }}>
                        <button
                          onClick={function() {
                            if (cartQty <= 1) { cartCtx.removeFromCart(listing._id); window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Removed from cart', type: 'info' } })); }
                            else { cartCtx.updateCartQuantity(listing._id, cartQty - 1, maxQty); }
                          }}
                          className="tw-px-2.5 tw-py-2.5 tw-bg-transparent tw-border-0 tw-text-green-700 dark:tw-text-green-400 hover:tw-bg-green-100 dark:hover:tw-bg-green-900/30 tw-transition-colors tw-cursor-pointer tw-font-bold tw-text-sm tw-leading-none"
                        >
                          {cartQty <= 1 ? <svg className="tw-w-3.5 tw-h-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg> : '\u2212'}
                        </button>
                        <span className="tw-px-2.5 tw-py-2.5 tw-font-bold tw-text-green-700 dark:tw-text-green-400 tw-min-w-[28px] tw-text-center">{cartQty}</span>
                        <button
                          onClick={function() { if (cartQty < maxQty) { cartCtx.updateCartQuantity(listing._id, cartQty + 1, maxQty); } }}
                          disabled={cartQty >= maxQty}
                          className={"tw-px-2.5 tw-py-2.5 tw-bg-transparent tw-border-0 tw-font-bold tw-text-sm tw-leading-none tw-transition-colors" + (cartQty >= maxQty ? " tw-text-green-300 dark:tw-text-green-800 tw-cursor-default" : " tw-text-green-700 dark:tw-text-green-400 hover:tw-bg-green-100 dark:hover:tw-bg-green-900/30 tw-cursor-pointer")}
                        >
                          +
                        </button>
                      </span>
                    );
                  }
                  return (
                    <button
                      onClick={() => this.handleAddToCart(listing, cartCtx)}
                      className="tw-flex-1 tw-border tw-border-brand-gold tw-bg-transparent tw-text-brand-gold hover:tw-bg-brand-gold/10 tw-font-bold tw-py-2.5 tw-px-4 tw-rounded-lg tw-text-sm tw-transition-colors tw-cursor-pointer"
                    >
                      Add to Cart
                    </button>
                  );
                }}
              </CartContext.Consumer>
              <Link
                to={`/purchase/${listing._id}`}
                className="tw-flex-1 tw-text-center tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-font-bold tw-py-2.5 tw-px-4 tw-rounded-lg tw-text-sm tw-transition-colors"
                style={{ textDecoration: 'none', color: '#000' }}
              >
                Buy Now
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  renderBuyerProtection() {
    return (
      <a href="/more#buyer-protection" target="_blank" rel="noopener noreferrer" className="tw-block tw-mb-6 tw-rounded-xl tw-border-2 tw-border-brand-gold/40 tw-bg-brand-gold/5 dark:tw-bg-brand-gold/10 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-3 hover:tw-border-brand-gold/70 tw-transition-colors">
        <svg className="tw-w-6 tw-h-6 tw-text-brand-gold tw-flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
        </svg>
        <div>
          <span className="tw-text-sm tw-font-semibold tw-text-brand-gold">Buyer Protection</span>
          <span className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-ml-2">
            Every purchase is covered by Primal Marketplace's buyer guarantee
          </span>
        </div>
      </a>
    );
  }

  renderPriceDropAlert() {
    const { alertSet, alertFoilOnly } = this.state;
    var card = this.state.card || {};
    var hasFoil = !card.rarity || LEGACY_NO_FOIL_RARITIES.indexOf(card.rarity) === -1;
    return (
      <div className="tw-mt-3">
        {hasFoil && alertSet && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
          <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500">Notify for:</span>
          <button
            onClick={() => { if (this.state.alertFoilOnly) this.toggleAlertFoilOnly(); }}
            className={`tw-py-1 tw-px-2.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors tw-border tw-cursor-pointer ${
              !alertFoilOnly
                ? 'tw-border-brand-gold tw-text-brand-gold tw-bg-brand-gold/10'
                : 'tw-border-[#ddd] dark:tw-border-[#444] tw-text-[#999] dark:tw-text-gray-500 tw-bg-transparent'
            }`}
          >
            All listings
          </button>
          <button
            onClick={() => { if (!this.state.alertFoilOnly) this.toggleAlertFoilOnly(); }}
            className={`tw-py-1 tw-px-2.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors tw-border tw-cursor-pointer ${
              alertFoilOnly
                ? 'tw-border-brand-gold tw-text-brand-gold tw-bg-brand-gold/10'
                : 'tw-border-[#ddd] dark:tw-border-[#444] tw-text-[#999] dark:tw-text-gray-500 tw-bg-transparent'
            }`}
          >
            Foil only
          </button>
        </div>
        )}
        <button
          onClick={() => this.togglePriceAlert()}
          className={`tw-py-1.5 tw-px-4 tw-rounded-lg tw-text-xs tw-font-medium tw-transition-colors tw-border ${
            alertSet
              ? 'tw-bg-green-50 dark:tw-bg-green-900/20 tw-border-green-300 dark:tw-border-green-700 tw-text-green-700 dark:tw-text-green-400'
              : 'tw-bg-transparent tw-border-[#ddd] dark:tw-bg-[#252525] dark:tw-border-brand-gold/40 tw-text-[#999] dark:tw-text-white hover:tw-border-brand-gold hover:tw-text-brand-gold'
          }`}
        >
          {alertSet ? (
            <span>&#10003; Notification set{alertFoilOnly ? ' (Foil)' : ''} &middot; <span className="tw-underline">Remove</span></span>
          ) : (
            <span>&#128276;&nbsp; Notify me when price drops</span>
          )}
        </button>
      </div>
    );
  }

  renderPriceHistory() {
    const { history, historyFilter, showPriceHistory } = this.state;
    var condMap = { 'NM': 'Near Mint', 'NMF': 'Near Mint Foil', 'LP': 'Lightly Played', 'LPF': 'Lightly Played Foil', 'MP': 'Moderately Played', 'MPF': 'Moderately Played Foil', 'HP': 'Heavily Played', 'HPF': 'Heavily Played Foil' };
    var self = this;
    var filteredHistory = history.filter(function(h) {
      if (historyFilter === 'all') return true;
      return h.condition === historyFilter;
    });
    if (filteredHistory.length === 0) filteredHistory = history;
    return (
      <div className="tw-mt-4">
        <button
          onClick={function() { self.setState({ showPriceHistory: !showPriceHistory }); }}
          className="tw-flex tw-items-center tw-justify-between tw-w-full tw-bg-transparent tw-border-0 tw-cursor-pointer tw-py-1 tw-px-0"
        >
          <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-font-semibold tw-tracking-wider">
            Price History
            {history.length > 0 && !showPriceHistory && (
              <span className="tw-normal-case tw-tracking-normal tw-text-[#bbb] dark:tw-text-gray-600 tw-ml-2">
                {history.length} sale{history.length !== 1 ? 's' : ''}
              </span>
            )}
          </span>
          <svg
            className="tw-w-4 tw-h-4 tw-text-[#999] dark:tw-text-gray-500 tw-transition-transform"
            style={{ transform: showPriceHistory ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className={'collapsible' + (showPriceHistory ? ' collapsible-expanded' : '')}>
          <div className="tw-pt-2">
            <PriceHistoryChart history={history} onFilterChange={function(f) { self.setState({ historyFilter: f }); }} />
            {filteredHistory.length > 1 && (
              <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-[#e0e0e0] dark:tw-border-[#333]">
                <p className="tw-text-[10px] tw-text-[#bbb] dark:tw-text-gray-600 tw-uppercase tw-font-semibold tw-tracking-wider tw-mb-1.5">
                  Recent Sales
                </p>
                <div className="tw-space-y-1">
                  {filteredHistory.slice(0, 3).map((h, i) => (
                    <div key={i} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                      <div className="tw-flex tw-items-center tw-gap-1.5">
                        <span className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">
                          ${h.price.toFixed(2)}
                        </span>
                        {h.condition && historyFilter === 'all' && (
                          <span className="tw-text-[9px] tw-text-[#999] dark:tw-text-gray-500">{condMap[h.condition] || h.condition}</span>
                        )}
                      </div>
                      <span className="tw-text-[#bbb] dark:tw-text-gray-600">
                        {new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderRelatedCards() {
    const { relatedCards, card } = this.state;
    if (!relatedCards || relatedCards.length === 0) return null;

    return (
      <div className="tw-mt-10">
        <style>{`
          .related-scroll::-webkit-scrollbar { height: 6px; }
          .related-scroll::-webkit-scrollbar-track { background: transparent; border-radius: 3px; }
          .related-scroll::-webkit-scrollbar-thumb { background: #d1d1d1; border-radius: 3px; }
          .related-scroll::-webkit-scrollbar-thumb:hover { background: #bbb; }
          .dark .related-scroll::-webkit-scrollbar-thumb { background: #333; }
          .dark .related-scroll::-webkit-scrollbar-thumb:hover { background: #444; }
        `}</style>
        <h2 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-4">
          More from {card.cardSet || 'this set'}
        </h2>
        <div
          className="related-scroll tw-flex tw-gap-4 tw-overflow-x-auto tw-pb-4 tw-snap-x tw-snap-mandatory"
          style={{ scrollbarWidth: 'thin', scrollbarColor: document.documentElement.classList.contains('tw-dark') ? '#333 transparent' : '#d1d1d1 transparent' }}
        >
          {relatedCards.map(rc => {
            const rcImage = resolveCardImage(rc.image);
            const rcHoriz = rc.horizontal === true;
            return (
              <Link
                key={rc._id}
                to={`/card/${rc._id}`}
                className={'tw-flex-shrink-0 tw-snap-start tw-group ' + (rcHoriz ? 'tw-w-56' : 'tw-w-36')}
                style={{ textDecoration: 'none' }}
              >
                <div className={(rcHoriz ? 'tw-aspect-[4/3]' : 'tw-aspect-[3/4]') + ' tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-mb-2 group-hover:tw-border-brand-gold tw-transition-colors tw-relative'}>
                  <LazyCardImage src={rcImage} alt={rc.name} fallbackSize="tw-w-12 tw-h-12" eager rotate={rcHoriz ? (rc.rotation || -90) : 0} />
                  {rc.lowestPrice != null ? (
                    <div
                      className="tw-absolute tw-bottom-1.5 tw-left-1.5 tw-text-brand-gold tw-text-xs tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-full tw-shadow-lg"
                      style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
                    >
                      ${rc.lowestPrice.toFixed(2)}
                    </div>
                  ) : rc.listingCount === 0 ? (
                    <div
                      className="tw-absolute tw-bottom-1.5 tw-left-1.5 tw-text-[11px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full"
                      style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#999' }}
                    >
                      No listings
                    </div>
                  ) : null}
                </div>
                <p className="tw-text-xs tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white group-hover:tw-text-brand-gold tw-transition-colors tw-truncate tw-mb-0.5">
                  {rc.name}
                </p>
                {renderRarityBadge(rc.rarity, null)}
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  renderCommunityTags() {
    const { tags, showTagInput, tagInputValue, showAllTags, showCommunityTags } = this.state;
    const isLoggedIn = Auth.loggedIn();
    const TAG_LIMIT = 6;
    const visibleTags = showAllTags ? tags : tags.slice(0, TAG_LIMIT);
    const hiddenCount = tags.length - TAG_LIMIT;

    return (
      <div className="tw-mt-6">
        <button
          onClick={() => this.setState({ showCommunityTags: !showCommunityTags })}
          className="tw-flex tw-items-center tw-justify-between tw-w-full tw-bg-transparent tw-border-0 tw-cursor-pointer tw-py-1 tw-px-0 tw-mb-2"
        >
          <h2 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0">
            Community Tags
            {tags.length > 0 && !showCommunityTags && (
              <span className="tw-text-xs tw-font-normal tw-text-[#999] dark:tw-text-gray-500 tw-ml-2">
                ({tags.length})
              </span>
            )}
          </h2>
          <svg
            className="tw-w-4 tw-h-4 tw-text-[#999] dark:tw-text-gray-500"
            style={{ transform: showCommunityTags ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className={'collapsible' + (showCommunityTags ? ' collapsible-expanded' : '')}>
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-3">
          {visibleTags.map((t, i) => (
            <button
              key={i}
              onClick={() => isLoggedIn && this.toggleTag(t)}
              disabled={!isLoggedIn}
              className={`tw-px-3 tw-py-1.5 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-transition-colors ${
                t.userVoted
                  ? 'tw-bg-brand-gold/20 tw-border-brand-gold tw-text-brand-gold'
                  : 'tw-bg-[#f5f5f5] dark:tw-bg-[#252525] tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#666] dark:tw-text-gray-400 hover:tw-border-brand-gold hover:tw-text-brand-gold'
              } ${!isLoggedIn ? 'tw-cursor-default' : 'tw-cursor-pointer'}`}
            >
              {t.tag} ({t.count})
            </button>
          ))}
          {tags.length === 0 && (
            <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-0">
              No tags yet. Be the first to tag this card!
            </p>
          )}
          {hiddenCount > 0 && (
            <button
              onClick={() => this.setState({ showAllTags: !showAllTags })}
              className="tw-px-3 tw-py-1.5 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-border-dashed tw-border-[#ccc] dark:tw-border-[#444] tw-text-[#999] dark:tw-text-gray-500 hover:tw-border-brand-gold hover:tw-text-brand-gold tw-transition-colors tw-cursor-pointer tw-bg-transparent"
            >
              {showAllTags ? 'Show less' : `+${hiddenCount} more`}
            </button>
          )}
        </div>

        {isLoggedIn ? (
          <div>
            {!showTagInput ? (
              <button
                onClick={() => this.setState({ showTagInput: true })}
                className="tw-text-xs tw-font-medium tw-text-brand-gold hover:tw-text-brand-gold-dark tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
              >
                + Add Tag
              </button>
            ) : (
              <div className="tw-mt-2">
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                  {TAG_SUGGESTIONS.map(s => (
                    <button
                      key={s}
                      onClick={() => this.addTag(s)}
                      className="tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-bg-[#f5f5f5] dark:tw-bg-[#252525] tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#666] dark:tw-text-gray-400 hover:tw-border-brand-gold hover:tw-text-brand-gold tw-transition-colors tw-cursor-pointer"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => this.setState({ showTagInput: false, tagInputValue: '' })}
                  className="tw-text-xs tw-text-[#999] hover:tw-text-[#666] dark:tw-text-gray-500 tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-mt-1"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => this.context.openAuthModal('login', () => this.fetchTags())}
            className="tw-text-xs tw-font-medium tw-text-brand-gold hover:tw-text-brand-gold-dark tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
          >
            Log in to tag this card
          </button>
        )}
        </div>
      </div>
    );
  }

  render() {
    const { card, loading, error } = this.state;
    const listings = this.getSortedListings();
    const { listingPage, listingsPerPage } = this.state;
    const totalPages = Math.ceil(listings.length / listingsPerPage);
    const paginatedListings = listings.slice(listingPage * listingsPerPage, (listingPage + 1) * listingsPerPage);

    if (loading) return this.renderLoading();

    if (error || !card) {
      return (
        <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-20 tw-text-center">
          <p className="tw-text-lg tw-text-[#999] dark:tw-text-gray-500 tw-mb-4">
            {error || 'Card not found.'}
          </p>
          <Link
            to="/"
            className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline tw-font-medium tw-transition-colors"
          >
            Back to Home
          </Link>
        </div>
      );
    }

    const imageUrl = resolveCardImage(card.image);

    const lowestPrice = card.lowestPrice;
    const listingCount = listings.length;

    // Helper: calculate median of an array of numbers
    function calcMedian(arr) {
      if (arr.length === 0) return null;
      var sorted = arr.slice().sort(function(a, b) { return a - b; });
      var mid = Math.floor(sorted.length / 2);
      return sorted.length === 1 ? sorted[0]
        : sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }

    // Helper: calculate market price from sales + listings for a given foil filter
    function calcMarketPrice(sales, activeListings, isFoil) {
      var filteredSales = sales.filter(function(h) { return isFoil === null || (!!h.foil) === isFoil; }).slice(0, 10);
      if (filteredSales.length > 0) {
        return calcMedian(filteredSales.map(function(h) { return h.price; }));
      }
      var filteredListings = activeListings.filter(function(l) { return isFoil === null || (!!l.foil) === isFoil; });
      if (filteredListings.length === 1) return filteredListings[0].price;
      if (filteredListings.length > 1) {
        return calcMedian(filteredListings.map(function(l) { return l.price; }));
      }
      return null;
    }

    var allSales = (this.state.history || []);
    var regularListings = listings.filter(function(l) { return !l.foil; });
    var foilListings = listings.filter(function(l) { return l.foil; });
    var hasFoil = foilListings.length > 0 || allSales.some(function(h) { return h.foil; });
    var hasRegular = regularListings.length > 0 || allSales.some(function(h) { return !h.foil; });

    // Overall market price (all listings combined)
    var marketPrice = calcMarketPrice(allSales, listings, null);
    // Separate foil/non-foil prices
    var regularPrice = hasRegular ? calcMarketPrice(allSales, listings, false) : null;
    var foilPrice = hasFoil ? calcMarketPrice(allSales, listings, true) : null;

    return (
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-6 md:tw-py-10">
        <Helmet>
          <title>{(card.name || 'Card') + ' | Primal Marketplace'}</title>
          <meta name="description" content={`Buy ${card.name} from the Primal Marketplace. ${listingCount} listing${listingCount !== 1 ? 's' : ''} available.`} />
          <meta property="og:title" content={`${card.name} | Primal Marketplace`} />
          <meta property="og:description" content={`${listingCount} listing${listingCount !== 1 ? 's' : ''} available from $${lowestPrice ? lowestPrice.toFixed(2) : '\u2014'}`} />
          <meta property="og:type" content="product" />
          {imageUrl && <meta property="og:image" content={imageUrl} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${card.name} | Primal Marketplace`} />
          <meta name="twitter:description" content={`${listingCount} listing${listingCount !== 1 ? 's' : ''} available from $${lowestPrice ? lowestPrice.toFixed(2) : '\u2014'}`} />
          {imageUrl && <meta name="twitter:image" content={imageUrl} />}
          {card.name && (
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": card.name,
              "image": imageUrl || undefined,
              "description": `Buy ${card.name} from the Primal Marketplace.`,
              "offers": lowestPrice ? {
                "@type": "AggregateOffer",
                "lowPrice": lowestPrice.toFixed(2),
                "priceCurrency": "USD",
                "offerCount": listingCount,
                "availability": listingCount > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
              } : undefined
            })}</script>
          )}
        </Helmet>

        {/* Breadcrumb */}
        <nav className="tw-mb-6 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-pl-0 tw-overflow-hidden tw-w-full">
          <Link to="/" className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors">
            Home
          </Link>
          <span className="tw-flex-shrink-0">/</span>
          {card.cardSet && (
            <>
              <Link to={`/browse/${encodeURIComponent(card.cardSet)}`} className="tw-truncate tw-min-w-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors" style={{ maxWidth: '35vw' }}>
                {card.cardSet}
              </Link>
              <span className="tw-flex-shrink-0">/</span>
            </>
          )}
          <span className="tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-min-w-0">{card.name}</span>
        </nav>

        {/* Horizontal card: blurred backdrop hero */}
        {card.horizontal && (
          <div className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-mb-4" style={{ minHeight: '280px' }}>
            {/* Blurred background */}
            <img
              src={imageUrl}
              alt=""
              crossOrigin="anonymous"
              className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
              style={{ filter: 'blur(40px) saturate(1.2)', transform: 'scale(1.2)', opacity: 0.35 }}
            />
            {/* Dark overlay */}
            <div className="tw-absolute tw-inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }} />
            {/* Sharp card image */}
            <div key={imageUrl} className="tw-relative tw-flex tw-items-center tw-justify-center tw-py-8 tw-px-4" style={{ animation: 'pvFadeIn 0.3s ease' }}>
              <div className="tw-relative tw-aspect-[3/2] tw-overflow-hidden tw-rounded-lg" style={{ maxWidth: '600px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
                <img
                  src={imageUrl}
                  alt={card.name}
                  crossOrigin="anonymous"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    height: '150%',
                    transform: 'translate(-50%, -50%) rotate(' + (card.rotation || -90) + 'deg)',
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Card Info Section */}
        <div className={'tw-flex tw-flex-col tw-gap-8 ' + (card.horizontal ? '' : 'md:tw-flex-row md:tw-gap-10')}>

          {/* LEFT: Card Image (vertical cards only) */}
          {!card.horizontal && (
          <div className="tw-w-full tw-flex-shrink-0 md:tw-w-[45%]">
            <div className="tw-relative tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-aspect-square">
              {imageUrl ? (
                <>
                  <img
                    src={imageUrl}
                    alt=""
                    className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
                    style={{ filter: 'blur(40px) saturate(1.2)', transform: 'scale(1.2)', opacity: 0.35 }}
                  />
                  <img
                    key={imageUrl}
                    src={imageUrl}
                    alt={card.name}
                    crossOrigin="anonymous"
                    className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-contain tw-z-10"
                    style={{ animation: 'pvFadeIn 0.3s ease' }}
                  />
                </>
              ) : (
                <LazyCardImage src={imageUrl} alt={card.name} fallbackSize="tw-w-24 tw-h-24" />
              )}
            </div>
          </div>
          )}

          {/* RIGHT: Card Details */}
          <div className="tw-flex-1 tw-min-w-0">
            {/* Card Name */}
            <h1 className="tw-text-xl md:tw-text-3xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-3 tw-leading-tight">
              {card.name}
            </h1>

            {/* Set + Number */}
            {(card.cardSet || card.cardNumber) && (
              <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-3">
                {card.cardSet}{card.cardNumber ? ` \u00B7 #${card.cardNumber}` : ''}
              </p>
            )}

            {/* Errata / Variant badges */}
            {(card.errata || card.variant) && (
              <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-3">
                {card.errata && (
                  <span className="tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-orange-100 dark:tw-bg-orange-900/30 tw-text-orange-700 dark:tw-text-orange-400 tw-border tw-border-orange-200 dark:tw-border-orange-800">
                    Errata
                  </span>
                )}
                {card.variant && (
                  <span className="tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-brand-gold/15 tw-text-brand-gold tw-border tw-border-brand-gold/30">
                    {card.variant}
                  </span>
                )}
              </div>
            )}

            {/* Badges: Rarity + Elements + CardType */}
            <GameContext.Consumer>
              {(gameCtx) => {
                const gameConfig = gameCtx && gameCtx.currentGame;
                return (
                  <>
                    <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
                      {renderRarityBadge(card.rarity, gameConfig && gameConfig.rarities)}
                      {this.renderElementBadges(card.element)}
                      {card.cardType && (
                        <span className="tw-text-xs tw-font-medium tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-[#f5f5f5] dark:tw-bg-[#252525] tw-text-[#666] dark:tw-text-gray-400 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333]">
                          {card.cardType}
                        </span>
                      )}
                      {card.playable === false && (
                        <span className="tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-red-100 dark:tw-bg-red-900/30 tw-text-red-700 dark:tw-text-red-400 tw-border tw-border-red-200 dark:tw-border-red-800">
                          Not Tournament Legal
                        </span>
                      )}
                    </div>

                    {/* Dynamic card fields from game config */}
                    {this.renderDynamicFields(card, gameConfig)}
                  </>
                );
              }}
            </GameContext.Consumer>

            {/* Market Price Summary */}
            <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-p-6 tw-mt-2 tw-mb-6">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-font-semibold tw-tracking-wider tw-mb-0">
                  Market Price
                </p>
                {listingCount > 0 && (
                  <button
                    onClick={() => { if (this.listingsSectionRef.current) { this.listingsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } }}
                    className="tw-text-xs tw-font-semibold tw-text-brand-gold hover:tw-text-brand-gold-dark tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0"
                  >
                    View Listings &darr;
                  </button>
                )}
              </div>
              {marketPrice !== null ? (
                <div>
                  {/* Show separate prices when both foil and non-foil exist */}
                  {hasFoil && hasRegular ? (
                    <div>
                      <div className="tw-flex tw-items-baseline tw-gap-4 tw-flex-wrap">
                        {regularPrice !== null && (
                          <div>
                            <span className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
                              ${regularPrice.toFixed(2)}
                            </span>
                            <span className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-ml-1.5">
                              Regular
                            </span>
                          </div>
                        )}
                        {foilPrice !== null && (
                          <div>
                            <span className="tw-text-2xl tw-font-bold tw-text-brand-gold">
                              ${foilPrice.toFixed(2)}
                            </span>
                            <span className="tw-text-xs tw-text-brand-gold/70 tw-ml-1.5">
                              Foil
                            </span>
                          </div>
                        )}
                      </div>
                      <span className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mt-1 tw-block">
                        {regularListings.length} regular, {foilListings.length} foil listing{foilListings.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <span className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
                        ${marketPrice.toFixed(2)}
                      </span>
                      {hasFoil && (
                        <span className="tw-text-xs tw-text-brand-gold/70 tw-ml-1.5">Foil</span>
                      )}
                      <span className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-ml-2">
                        {listingCount} listing{listingCount !== 1 ? 's' : ''} available
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-0">
                  No listings yet
                </p>
              )}

              {/* Price Drop Alert Button */}
              {this.renderPriceDropAlert()}

              {/* Price History */}
              {this.renderPriceHistory()}
            </div>

            {/* BUYLIST FEATURE: disabled — preserved for future use
            {this.state.buylistDemand && this.state.buylistDemand.buyerCount > 0 && (
              <div className="tw-bg-[#f0fdf4] dark:tw-bg-[#0a1f0d] tw-rounded-xl tw-border tw-border-[#bbf7d0] dark:tw-border-[#166534] tw-p-5 tw-mt-4">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-semibold" style={{ background: '#16a34a', color: '#fff' }}>
                    <i className="fa fa-shopping-cart"></i>
                    {this.state.buylistDemand.buyerCount} buyer{this.state.buylistDemand.buyerCount !== 1 ? 's' : ''} want{this.state.buylistDemand.buyerCount === 1 ? 's' : ''} this card
                  </span>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <div>
                    <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-m-0 tw-mb-0.5">Highest Offer</p>
                    <p className="tw-text-lg tw-font-bold tw-m-0" style={{ color: '#16a34a' }}>
                      ${this.state.buylistDemand.highestPrice.toFixed(2)}
                    </p>
                  </div>
                  <div className="tw-text-right">
                    <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-m-0 tw-mb-0.5">Total Wanted</p>
                    <p className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">
                      {this.state.buylistDemand.totalQuantity}
                    </p>
                  </div>
                </div>

                {buyer entries, expand/collapse, sell buttons, add to buylist button...}
              </div>
            )}
            */}

            {/* Community Tags */}
            {this.renderCommunityTags()}
          </div>
        </div>

        {/* Listings Section */}
        <div className="tw-mt-12" ref={this.listingsSectionRef}>
          {/* Buyer Protection Banner */}
          <div className="tw-mt-6">{this.renderBuyerProtection()}</div>

          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h2 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-leading-none" style={{ margin: 0 }}>
              Listings{listingCount > 0 ? ` (${listingCount})` : ''}
            </h2>
            {listingCount > 1 && (
              <div className="tw-relative">
                <button
                  onClick={() => this.setState({ sortOpen: !this.state.sortOpen })}
                  className="tw-flex tw-items-center tw-gap-2 tw-bg-[#f5f5f5] dark:tw-bg-[#252525] tw-border tw-border-solid tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-text-[#1a1a1a] dark:tw-text-white tw-cursor-pointer tw-transition-colors hover:tw-border-brand-gold"
                >
                  <span className="tw-text-[#999] dark:tw-text-gray-500">Sort:</span>
                  <span>{SORT_OPTIONS.find(o => o.value === this.state.sortBy + '-' + this.state.sortDir)?.label || 'Price: Low to High'}</span>
                  <svg className="tw-w-3.5 tw-h-3.5 tw-text-[#999]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {this.state.sortOpen && (
                  <>
                    <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => this.setState({ sortOpen: false })} />
                    <div className="tw-absolute tw-left-0 sm:tw-left-auto sm:tw-right-0 tw-top-full tw-mt-1 tw-z-20 tw-bg-white dark:tw-bg-[#1a1a1a] tw-border tw-border-solid tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-shadow-lg tw-py-1 tw-min-w-[200px]">
                      {SORT_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => {
                            const [field, dir] = opt.value.split('-');
                            this.setState({ sortBy: field, sortDir: dir, sortOpen: false });
                            this.goToPage(0);
                          }}
                          className={`tw-w-full tw-text-left tw-px-4 tw-py-2.5 tw-text-sm tw-transition-colors tw-cursor-pointer tw-border-0 ${
                            this.state.sortBy + '-' + this.state.sortDir === opt.value
                              ? 'tw-bg-brand-gold/10 tw-text-brand-gold tw-font-medium'
                              : 'tw-bg-transparent tw-text-[#1a1a1a] dark:tw-text-white hover:tw-bg-[#f5f5f5] dark:hover:tw-bg-[#252525]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {listingCount === 0 ? (
            <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-py-12 tw-text-center">
              <p className="tw-text-[#999] dark:tw-text-gray-500 tw-mb-4">
                No one is selling this card yet.
              </p>
              {Auth.loggedIn() ? (
                <Link
                  to={{ pathname: '/sell', state: { prefillCard: this.state.card } }}
                  className="tw-inline-block tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-3 tw-px-8 tw-rounded-lg tw-text-sm tw-no-underline tw-transition-colors"
                >
                  Sell This Card
                </Link>
              ) : (
                <button
                  onClick={() => this.context.openAuthModal('login', () => this.props.history.push({ pathname: '/sell', state: { prefillCard: this.state.card } }))}
                  className="tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-3 tw-px-8 tw-rounded-lg tw-text-sm tw-transition-colors tw-border-0 tw-cursor-pointer"
                >
                  Sell This Card
                </button>
              )}
            </div>
          ) : (
            <>
              <div ref={el => { this.listingsRef = el; }} style={{ opacity: this.state.listingFading ? 0 : 1, transition: 'opacity 150ms ease', minHeight: this.listingsMinHeight ? this.listingsMinHeight + 'px' : undefined }}>
                {/* Desktop Table */}
                <div className="tw-hidden md:tw-block tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden">
                  <table className="tw-w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="tw-border-b tw-border-[#e0e0e0] dark:tw-border-[#333]">
                        <th className="tw-text-left tw-text-xs tw-font-semibold tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-tracking-wider tw-py-3 tw-px-3">
                          Seller
                        </th>
                        <th className="tw-text-left tw-text-xs tw-font-semibold tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-tracking-wider tw-py-3 tw-px-3">
                          Condition
                        </th>
                        <th className="tw-text-left tw-text-xs tw-font-semibold tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-tracking-wider tw-py-3 tw-px-3">
                          Price
                        </th>
                        <th className="tw-text-left tw-text-xs tw-font-semibold tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-tracking-wider tw-py-3 tw-px-3">
                          Shipping
                        </th>
                        <th className="tw-py-3 tw-px-3" />
                      </tr>
                    </thead>
                    <CartContext.Consumer>
                      {(cartCtx) => (
                        <tbody>
                          {paginatedListings.map(listing => this.renderListingRow(listing, cartCtx.cart))}
                        </tbody>
                      )}
                    </CartContext.Consumer>
                  </table>
                </div>

                {/* Mobile Stacked Cards */}
                <CartContext.Consumer>
                  {(cartCtx) => (
                    <div className="tw-flex tw-flex-col tw-gap-3 md:tw-hidden">
                      {paginatedListings.map(listing => this.renderListingCard(listing, cartCtx.cart))}
                    </div>
                  )}
                </CartContext.Consumer>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-mt-4 tw-flex-wrap">
                  <button
                    onClick={() => this.goToPage(this.state.listingPage - 1)}
                    disabled={this.state.listingPage === 0}
                    className={`tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-border tw-border-solid tw-transition-colors ${
                      this.state.listingPage === 0
                        ? 'tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#ccc] dark:tw-text-[#444] tw-bg-transparent tw-cursor-default'
                        : 'tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#1a1a1a] dark:tw-text-white tw-bg-transparent tw-cursor-pointer hover:tw-border-brand-gold hover:tw-text-brand-gold'
                    }`}
                  >
                    &lsaquo;
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => {
                    const current = this.state.listingPage;
                    if (totalPages <= 7 || i === 0 || i === totalPages - 1 || (i >= current - 1 && i <= current + 1)) {
                      return (
                        <button
                          key={i}
                          onClick={() => this.goToPage(i)}
                          className={`tw-w-8 tw-h-8 tw-rounded-lg tw-text-sm tw-font-medium tw-border tw-border-solid tw-transition-colors tw-cursor-pointer ${
                            i === current
                              ? 'tw-border-brand-gold tw-bg-brand-gold/10 tw-text-brand-gold'
                              : 'tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#1a1a1a] dark:tw-text-white tw-bg-transparent hover:tw-border-brand-gold hover:tw-text-brand-gold'
                          }`}
                        >
                          {i + 1}
                        </button>
                      );
                    }
                    if (i === current - 2 || i === current + 2) {
                      return <span key={i} className="tw-text-[#999] dark:tw-text-gray-500 tw-text-sm tw-px-1">&hellip;</span>;
                    }
                    return null;
                  })}
                  <button
                    onClick={() => this.goToPage(this.state.listingPage + 1)}
                    disabled={this.state.listingPage >= totalPages - 1}
                    className={`tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-text-sm tw-font-medium tw-border tw-border-solid tw-transition-colors ${
                      this.state.listingPage >= totalPages - 1
                        ? 'tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#ccc] dark:tw-text-[#444] tw-bg-transparent tw-cursor-default'
                        : 'tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#1a1a1a] dark:tw-text-white tw-bg-transparent tw-cursor-pointer hover:tw-border-brand-gold hover:tw-text-brand-gold'
                    }`}
                  >
                    &rsaquo;
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Related Cards */}
        {this.renderRelatedCards()}

      </div>
    );
  }
}

export default CardDetail;
